import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  FormLabel,
  HStack,
  Icon,
  Spinner,
  Tag,
  TagLabel,
  Text,
  VStack,
} from '@chakra-ui/react'
import type { FieldProps, FormikHelpers, FormikProps } from 'formik'
import { Field, Formik } from 'formik'
import type { PropsWithChildren, ReactElement } from 'react'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useFetch, { CachePolicies } from 'use-http'
import { FiFileMinus } from 'react-icons/fi'
import _ from 'lodash'
import { colors, IKReactWindowedSelect } from '../../../../ui-kit'
import useToast from '../../../hooks/useToast'
import type {
  CmErrorObject,
  CmFieldResponseObject,
  MediaLibrarySearchFileResponse,
} from '../../../interfaces'
import { MLContext, UserContext } from '../../../providers'
import { PublicLinksInfoContext } from '../../../providers/PublicLinksInfoProvider'
import CMTextField from '../Details/CustomMetadataMlUploadForm/CMTextField'
import CMTextareaField from '../Details/CustomMetadataMlUploadForm/CMTextareaField'
import CMNumberField from '../Details/CustomMetadataMlUploadForm/CMNumberField'
import CMDateField from '../Details/CustomMetadataMlUploadForm/CMDateField'
import CMBooleanField from '../Details/CustomMetadataMlUploadForm/CMBooleanField'
import CMSingleSelectField from '../Details/CustomMetadataMlUploadForm/CMSingleSelectField'
import CMMultiSelectField from '../Details/CustomMetadataMlUploadForm/CMMultiSelectField'
import type { CMFieldProps } from '../Details/CustomMetadataMlUploadForm'
import { FeaturesEnum } from '../../../../utils/features'
import { CustomMetadataContext } from '../../../providers/CustomMetadataProvider'
import type { CmValues } from '../../../interfaces/FileDetailsResponse'
import { MediaLibraryActions } from '../../../providers/MLProvider'
import {
  getCmFieldValidatorMap,
  validateCmField,
} from '../NewButton/uploadUtils'
import type { CmAllowedValue } from '../../../interfaces/CustomMetadataResponse'

interface Props {
  isOpen: boolean
  selectedFiles: MediaLibrarySearchFileResponse[]
  onClose(): void
}

interface FormProps {
  upload: CmValues
}

interface DropdownOption {
  label: string
  value: string
}

interface BulkEditSuccessResponse {
  success: boolean
  imageMetadatas?: MediaLibrarySearchFileResponse[]
}

const ComponentMap: {
  [key: string]: (cmField: PropsWithChildren<CMFieldProps>) => ReactElement
} = {
  Text: CMTextField,
  Textarea: CMTextareaField,
  Number: CMNumberField,
  Date: CMDateField,
  Boolean: CMBooleanField,
  SingleSelect: CMSingleSelectField,
  MultiSelect: CMMultiSelectField,
}

function formatFieldType(type: string): string {
  switch (type) {
    case 'SingleSelect':
      return 'Single Select'
    case 'MultiSelect':
      return 'Multi Select'
    default:
      return type
  }
}

function getFormattedPlaceholder(fieldType) {
  switch (fieldType) {
    case 'SingleSelect':
      return 'Select a value'
    case 'MultiSelect':
      return 'Select value(s)'
    case 'Date':
      return 'Pick a date'
    case 'Boolean':
      return 'Select a value'
    case 'Number':
      return 'Enter a number'
    case 'Text':
      return 'Enter text'
    case 'Textarea':
      return 'Enter text'
    default:
      return ''
  }
}

const BulkEditCustomMetadataModal = ({
  isOpen,
  onClose,
  selectedFiles,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()
  const { isPublicLink } = useContext(PublicLinksInfoContext)
  const [formError, setFormError] = useState<string>('')
  const { response, request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })
  const { cmFields: allCMFs } = useContext(CustomMetadataContext)
  const { user } = useContext(UserContext)
  const [_mlContext, dispatch] = useContext(MLContext)
  const hasPathPolicyAccess = user?.pricingPlan.featuresList.includes(
    FeaturesEnum.PATH_POLICY,
  )

  const [cmfLoading, setCmfLoading] = useState<boolean>(false)
  const [cmFields, setCmFields] = useState<CmFieldResponseObject[]>([])

  const [cmFieldIdMap, setCmFieldIdMap] = useState<{
    [key: string]: CmFieldResponseObject
  }>({})
  const [bulkEditFieldMode, setBulkEditFieldMode] = useState<{
    [key: string]: 'set' | 'clear'
  }>({})
  const [selectedCMFields, setSelectedCMFields] = useState<
    CmFieldResponseObject[]
  >([])
  const [isFormSubmitted, setIsFormSubmitted] = useState<boolean>(false)

  const fileIdsKey = selectedFiles.map(f => f.fileId).join(',')

  const cmFieldValidatorMap = getCmFieldValidatorMap(cmFields)

  const fetchDataAndSetInState = async (
    uniqueFolderPaths: string[],
  ): Promise<void> => {
    let allFields: CmFieldResponseObject[] = []
    let res: CmFieldResponseObject[] | CmErrorObject
    if (hasPathPolicyAccess) {
      if (uniqueFolderPaths.length > 1) {
        const selectedFileIds = selectedFiles.map(file => file.fileId)
        res = (await request.post(
          `/api/media-library/${user?.clientNumber}/files/bulk/custom-metadata/supported-fields`,
          {
            fileIds: selectedFileIds,
          },
        )) as CmFieldResponseObject[] | CmErrorObject
      } else {
        const folderPath = uniqueFolderPaths[0]
        res = (await request.get(
          `/api/v1/customMetadataFields?folderPath=${encodeURIComponent(
            folderPath,
          )}`,
        )) as CmFieldResponseObject[] | CmErrorObject
      }
      if (request.error) {
        setCmFields([])
        setCmFieldIdMap({})
        setBulkEditFieldMode({})
        if ([400, 404].includes(response.status)) {
          const errMessage = (res as CmErrorObject).message
          if (
            errMessage.toLowerCase().includes('multiple path policies found')
          ) {
            setFormError(
              t(
                'bulk_edit_custom_metadata_path_policy_mismatch_error',
                // eslint-disable-next-line max-len
                'Bulk editing custom metadata requires all selected assets to share the same path policy. Select assets from the same folder or modify your selection to pick files that share same path policy and try again.',
              ),
            )
          } else {
            setFormError(errMessage)
          }
        } else {
          setFormError(
            t(
              'error_while_bulk_editing_custom_metadata',
              'Error while bulk editing custom metadata.',
            ),
          )
        }
        return
      }
      allFields = res as CmFieldResponseObject[]
    } else {
      allFields = allCMFs
    }
    const editableFields = allFields.filter(field => !field.schema.readOnly)
    if (editableFields.length === 0) {
      setFormError(
        t(
          'no_editable_custom_metadata_fields_found',
          'No editable custom metadata fields found for the selected assets.',
        ),
      )
      return
    }
    setFormError('')
    setCmFields(editableFields)
    setCmFieldIdMap(
      editableFields.reduce<{
        [key: string]: CmFieldResponseObject
      }>((acc, cmField) => {
        acc[cmField.id] = cmField
        return acc
      }, {}),
    )
    const fieldsMode = editableFields.reduce<{
      [key: string]: 'set' | 'clear'
    }>((acc, cmField) => {
      acc[cmField.name] = 'set'
      return acc
    }, {})
    setBulkEditFieldMode(fieldsMode)
  }

  const fieldValueValidator = async (
    values: FormProps,
    validationMode: 'submit' | 'change',
  ): Promise<{
    errors: { [key: string]: any }
    updateObject: { [key: string]: CmAllowedValue | null | undefined }
  }> => {
    const cmfsWithValues: CmFieldResponseObject[] = []
    const errors: { [key: string]: string } = {}
    const updateObject = selectedCMFields.reduce((obj, field) => {
      const fieldName = field.name
      const value = values.upload[fieldName]
      const mode = bulkEditFieldMode[fieldName]
      let input: CmAllowedValue | null | undefined
      if (mode === 'set') {
        input = value
      } else if (mode === 'clear') {
        input = null
      }
      if (input === null || input === undefined) {
        if (mode === 'set') {
          errors[fieldName] = t(
            'value_cannot_be_empty',
            'Value cannot be empty.',
          )
        }
      } else {
        cmfsWithValues.push(field)
      }
      obj[fieldName] = input
      return obj
    }, {})
    const yupErrors = await validateCmField(
      updateObject,
      validationMode === 'submit' ? selectedCMFields : cmfsWithValues,
      cmFieldValidatorMap,
      false,
    )
    let allErrors: { [key: string]: any }
    if (validationMode === 'submit') {
      allErrors = { ...errors, ...yupErrors }
    } else {
      allErrors = { ...yupErrors }
    }
    return { errors: allErrors, updateObject }
  }

  const onInputChange = async (
    cmField: CmFieldResponseObject,
    form: FormikProps<FormProps>,
    newValue?: CmAllowedValue,
  ): Promise<void> => {
    setIsFormSubmitted(false)
    const valuesToValidate: FormProps =
      newValue !== undefined
        ? {
            upload: {
              ...form.values.upload,
              [cmField.name]: newValue,
            },
          }
        : form.values
    const { errors } = await fieldValueValidator(valuesToValidate, 'change')
    const allErrors = { ...form.errors.upload, ...errors }
    if (_.isEmpty(errors[cmField.name])) {
      delete allErrors[cmField.name]
    }
    form.setErrors({ upload: allErrors })
    const errorValues = Object.values(allErrors).filter(Boolean)
    if (errorValues.length === 0) {
      setFormError('')
    }
  }

  const onModalClose = useCallback((): void => {
    onClose()
    setFormError('')
  }, [onClose, setFormError])

  const onFormSubmit = async (
    values: FormProps,
    helpers: FormikHelpers<FormProps>,
  ): Promise<void> => {
    const touchedFields = selectedCMFields.reduce<{
      [key: string]: boolean
    }>((obj, field) => {
      obj[field.name] = true
      return obj
    }, {})
    helpers.setTouched({
      upload: touchedFields,
    })
    const { errors, updateObject } = await fieldValueValidator(values, 'submit')
    if (!_.isEmpty(errors)) {
      helpers.setErrors({ upload: errors })
      setFormError(t('invalid_custom_metadata', 'Invalid custom metadata.'))
      return
    }
    const res = (await request.post(
      `/api/media-library/${user?.clientNumber}/files/bulk/custom-metadata`,
      {
        fileIds: selectedFiles.map(file => file.fileId),
        customMetadata: updateObject,
      },
    )) as BulkEditSuccessResponse | CmErrorObject
    if (request.error) {
      if ([400, 404].includes(response.status)) {
        const errorResponse = res as CmErrorObject
        setFormError(errorResponse.message)
        if (errorResponse.errors && _.isPlainObject(errorResponse.errors)) {
          helpers.setErrors({
            upload: errorResponse.errors,
          })
        }
      } else {
        setFormError(
          t(
            'error_while_bulk_editing_custom_metadata',
            'Error while bulk editing custom metadata.',
          ),
        )
      }
    } else {
      setFormError('')
      setIsFormSubmitted(true)
      const okRes = res as BulkEditSuccessResponse
      if (okRes.success) {
        toast({
          title: t(
            'custom_metadata_bulk_edit_success_title',
            'Custom metadata updated successfully.',
          ),
          status: 'success',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
        if (okRes.imageMetadatas) {
          dispatch([
            {
              type: MediaLibraryActions.UPDATE_FILES,
              payload: okRes.imageMetadatas,
            },
          ])
        }
      }
    }
  }

  useEffect(() => {
    const uniqueFolderPaths = Array.from(
      new Set(
        selectedFiles.map(file => {
          const pathParts = file.filePath.split('/')
          pathParts.pop()
          return pathParts.join('/')
        }),
      ),
    )

    if (selectedFiles.length > 0) {
      setCmfLoading(true)
      fetchDataAndSetInState(uniqueFolderPaths)
        .catch(() => {
          setFormError(
            t(
              'error_while_fetching_custom_metadata_fields',
              'Error while fetching custom metadata fields.',
            ),
          )
        })
        .finally(() => {
          setCmfLoading(false)
        })
    } else {
      setCmFields([])
      setCmFieldIdMap({})
      setSelectedCMFields([])
      setBulkEditFieldMode({})
      setFormError('')
    }
  }, [fileIdsKey])

  return (
    <Modal isOpen={isOpen} onClose={onModalClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <Formik<FormProps>
          initialValues={{
            upload: {},
          }}
          onSubmit={async (values, helpers) => {
            await onFormSubmit(values, helpers)
          }}
          validateOnChange={false}
          validateOnBlur={false}
          validateOnMount={false}
        >
          {({
            values,
            errors,
            isSubmitting,
            submitForm,
            setErrors,
            setValues,
          }) => (
            <>
              <ModalHeader>
                <Icon
                  as={FiFileMinus}
                  color="appBattleshipGrey"
                  backgroundColor="appPaleGrey"
                  boxSize={6}
                  padding="0.1em"
                  borderRadius="full"
                  marginRight="0.5em"
                />
                {t(
                  'bulk_edit_custom_metadata_modal_title',
                  'Edit custom metadata for {{count}} assets',
                  { count: selectedFiles.length },
                )}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                {formError && (
                  <Alert minW="unset" status="error" mt="1em" mb="1em">
                    <AlertIcon />
                    <AlertDescription>{formError}</AlertDescription>
                  </Alert>
                )}

                {!isPublicLink &&
                  (cmfLoading ? (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Spinner
                        thickness="4px"
                        speed="0.65s"
                        emptyColor="appPaleGrey"
                        color="appBlue"
                        size="xl"
                      />
                    </Box>
                  ) : (
                    <>
                      {cmFields.length > 0 && (
                        <Box mb="1em">
                          <IKReactWindowedSelect
                            placeholder={t(
                              'add_custom_metadata_field_to_edit',
                              'Add custom metadata field to edit',
                            )}
                            value={selectedCMFields.map(field => ({
                              label: field.label,
                              value: field.id,
                            }))}
                            variant="compact"
                            captureMenuScroll={false}
                            menuPlacement="auto"
                            isClearable
                            isSearchable
                            isMulti={true}
                            isLoading={cmfLoading}
                            isDisabled={
                              cmFields.length === 0 ||
                              isSubmitting ||
                              cmfLoading
                            }
                            closeMenuOnSelect={false}
                            options={cmFields
                              .sort((f1, f2) =>
                                f1.label.localeCompare(f2.label),
                              )
                              .map(field => ({
                                label: String(field.label),
                                value: field.id,
                                isDisabled: field.schema.readOnly,
                              }))}
                            formatOptionLabel={(option: any) => {
                              const field = cmFieldIdMap[option?.value]
                              return (
                                <HStack>
                                  <Text>{option?.label}</Text>
                                  <Tag
                                    size="sm"
                                    variant="subtle"
                                    colorScheme="blue"
                                    ml="auto"
                                  >
                                    <TagLabel>
                                      {formatFieldType(field.schema.type)}
                                    </TagLabel>
                                  </Tag>
                                  {field.schema.readOnly && (
                                    <Tag
                                      size="sm"
                                      variant="subtle"
                                      colorScheme="gray"
                                    >
                                      <TagLabel>
                                        {t('read_only', 'Read Only')}
                                      </TagLabel>
                                    </Tag>
                                  )}
                                </HStack>
                              )
                            }}
                            onChange={(newVal: DropdownOption[] | null) => {
                              const selectedFields = newVal?.map(
                                (option: DropdownOption) =>
                                  cmFieldIdMap[option.value],
                              )
                              const currentSelectedFieldIds = Array.from(
                                new Set(
                                  selectedCMFields.map(field => field.id),
                                ),
                              )
                              const removedFieldIds = new Set(
                                currentSelectedFieldIds
                                  .filter(
                                    id =>
                                      !selectedFields?.some(
                                        field => field.id === id,
                                      ),
                                  )
                                  .map(id => id),
                              )
                              removedFieldIds.forEach(id => {
                                const field = cmFieldIdMap[id]
                                // Clear the value from formik when field is removed
                                delete values.upload[field.name]
                                delete errors.upload?.[field.name]
                                setValues(values)
                                setErrors(errors)
                              })
                              const errorValues = Object.values(
                                errors.upload ?? {},
                              ).filter(Boolean)
                              if (errorValues.length === 0) {
                                setFormError('')
                              }
                              setSelectedCMFields(selectedFields ?? [])
                            }}
                          />
                        </Box>
                      )}
                      <VStack
                        width="full"
                        spacing="5"
                        flexGrow="1"
                        alignItems="flex-start"
                        py="4"
                        justifyContent="flex-start"
                        maxHeight="20em"
                        overflowY="scroll"
                        marginTop="1em"
                      >
                        {selectedCMFields.map(cmField => {
                          const FieldComponent =
                            ComponentMap[cmField.schema.type]
                          return (
                            <Field
                              name={cmField.name}
                              key={cmField.id}
                              width="full"
                            >
                              {({
                                form,
                              }: FieldProps<string | number | boolean>) => (
                                <Box
                                  p="1em"
                                  border="1px solid #E2E8F0"
                                  borderRadius="base"
                                  width="100%"
                                >
                                  <FieldComponent
                                    cmField={cmField}
                                    disabled={isSubmitting}
                                    readOnly={isSubmitting}
                                    form={form}
                                    isUpload={true}
                                    labelElement={
                                      <FormLabel marginRight="2em">
                                        {cmField.label}
                                      </FormLabel>
                                    }
                                    showBulkEditFieldModeDropdown={true}
                                    bulkEditFieldMode={bulkEditFieldMode}
                                    setBulkEditFieldMode={setBulkEditFieldMode}
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'flex-start',
                                    }}
                                    onInputChange={onInputChange}
                                    menuPosition="fixed"
                                    placeholder={getFormattedPlaceholder(
                                      cmField.schema.type,
                                    )}
                                  />
                                </Box>
                              )}
                            </Field>
                          )
                        })}
                      </VStack>
                    </>
                  ))}
              </ModalBody>
              <ModalFooter
                borderTop={`1px solid ${colors.appGrey}`}
                style={{ gap: '1em' }}
              >
                <Button onClick={onModalClose} variant="outline">
                  {t('cancel', 'Cancel')}
                </Button>
                <Button
                  type="submit"
                  isDisabled={
                    isSubmitting ||
                    Boolean(formError) ||
                    Object.values(errors.upload ?? {}).some(Boolean) ||
                    cmfLoading ||
                    selectedCMFields.length === 0
                  }
                  isLoading={isSubmitting}
                  onClick={async () => {
                    await submitForm()
                  }}
                >
                  {t('submit', 'Submit')}
                </Button>
              </ModalFooter>
            </>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

export default BulkEditCustomMetadataModal
